import { api } from '@carrent/api'
import { camera, offline } from '@carrent/platform'

/**
 * Сообщения чата через офлайн-очередь.
 *
 * ── Порядок тот же, что у приёмки ─────────────────────────────────
 *
 * Сначала всё ложится на устройство (текст — в задачу, фото — в
 * хранилище снимков со scope 'chat'), сообщение появляется в ленте
 * оптимистично со статусом «в очереди», и только потом очередь
 * пытается отправить. Обрыв стоит одной попытки, а не сообщения.
 *
 * ── Идемпотентность ───────────────────────────────────────────────
 *
 * client_message_id генерируется ЗДЕСЬ, один раз, при постановке —
 * и никогда не меняется. Сервер держит UNIQUE(conversation_id,
 * client_message_id): повтор задачи после частичного успеха
 * («сервер принял, ответ не дошёл») вернёт 200 с уже созданным
 * сообщением, и обработчик считает это успехом.
 *
 * ── Почему фото не в payload ──────────────────────────────────────
 *
 * То же правило, что у приёмки: Blob в задаче означал бы двойное
 * хранение. В задаче — только идентификаторы, multipart собирается
 * в момент отправки.
 *
 * ── 429 ───────────────────────────────────────────────────────────
 *
 * Rate limit сервера (30 сообщений/мин) обработчик очереди уже
 * трактует как временную ошибку (worker.isPermanent исключает 429):
 * задача повторится с backoff, а не умрёт.
 */

const { outbox, photos, messages } = offline

let seqCounter = 0
/** Монотонный порядок постановки в пределах сессии. */
const nextSeq = () => {
  seqCounter += 1
  return Date.now() * 1000 + (seqCounter % 1000)
}

const uuid = () =>
  globalThis.crypto?.randomUUID?.() ??
  `m-${Date.now()}-${Math.random().toString(36).slice(2)}`

/** Черновик-идентификатор фото сообщений диалога в хранилище снимков. */
export const chatDraftId = (conversationId) => `chat:${conversationId}`
const photoCategory = (clientMessageId, index) => `${clientMessageId}:${index}`

/**
 * Поставить сообщение в очередь.
 *
 * @param {object}  args
 * @param {number}  args.conversationId
 * @param {string}  args.body
 * @param {File[]}  [args.files]      — фото/документы
 * @param {number}  [args.replyToId]
 * @param {boolean} [args.asDocument] — мягкое сжатие для накладных/ТОРГ-2
 * @returns {{ task: object, message: object }} задача и временное
 *   сообщение для оптимистичного добавления в ленту.
 */
export async function enqueueChatMessage({
  conversationId, body = '', files = [], replyToId = null, asDocument = false,
}) {
  const clientMessageId = uuid()
  const draftId = chatDraftId(conversationId)
  const photoIds = []

  // Снимки — на устройство ДО постановки задачи: между этими шагами
  // приложение может закрыться, и задача без снимков ушла бы неполной.
  for (const [i, file] of files.entries()) {
    if (!file) continue
    const record = await photos.savePhoto({
      draftId,
      phase: 'msg',
      category: photoCategory(clientMessageId, i),
      file,
      scope: photos.SCOPE_CHAT,
      preset: asDocument ? camera.PRESET_DOCUMENT : null,
    })
    photoIds.push(record.id)
  }

  const task = await outbox.enqueue({
    type: outbox.TYPE_SEND_MESSAGE,
    draftId,
    // Двойное нажатие «отправить» на лагающем экране не ставит
    // вторую задачу — ключ у сообщения уже есть.
    dedupeKey: `chatmsg:${clientMessageId}`,
    payload: {
      conversation_id: conversationId,
      body,
      client_message_id: clientMessageId,
      reply_to_id: replyToId,
      photoIds,
      // Монотонный порядок: два сообщения в одну миллисекунду
      // по createdAt неразличимы, а порядок в ленте важен.
      seq: nextSeq(),
    },
  })

  return { task, message: tempMessage({ conversationId, body, clientMessageId, photoIds }) }
}

/**
 * Временное сообщение для ленты — до подтверждения сервером.
 *
 * id с префиксом tmp-: настоящий назначит сервер, и лента заменит
 * запись по client_message_id (дедупликация useConversation).
 */
export function tempMessage({ conversationId, body, clientMessageId, photoIds = [] }) {
  return {
    id: `tmp-${clientMessageId}`,
    conversation_id: conversationId,
    client_message_id: clientMessageId,
    kind: 'text',
    body,
    deleted: false,
    created_at: new Date().toISOString(),
    attachments: photoIds.map((pid) => ({ id: pid, local: true })),
    queued: true,
  }
}

/** Неотправленные сообщения диалога — для слияния с лентой при открытии. */
export async function queuedMessages(conversationId) {
  const tasks = await outbox.all()
  return tasks
    .filter(
      (t) =>
        t.type === outbox.TYPE_SEND_MESSAGE &&
        String(t.payload?.conversation_id) === String(conversationId) &&
        t.status !== outbox.DONE
    )
    .sort((a, b) => (a.payload.seq || 0) - (b.payload.seq || 0))
    .map((t) => ({
      ...tempMessage({
        conversationId,
        body: t.payload.body,
        clientMessageId: t.payload.client_message_id,
        photoIds: t.payload.photoIds,
      }),
      created_at: t.clientOccurredAt,
      failed: t.status === outbox.FAILED,
      taskId: t.id,
    }))
}

/**
 * Обработчики очереди для чата.
 *
 * Сборка multipart — в момент отправки, из хранилища снимков.
 * Фото удаляются ТОЛЬКО после подтверждения сервером: до этого
 * они единственная копия накладной.
 */
export function chatHandlers({ onMessageSent } = {}) {
  return {
    [outbox.TYPE_SEND_MESSAGE]: async (task) => {
      const p = task.payload
      const fd = new FormData()
      fd.append('body', p.body || '')
      fd.append('client_message_id', p.client_message_id)
      if (p.reply_to_id) fd.append('reply_to_id', String(p.reply_to_id))

      const stored = []
      for (const [i, pid] of (p.photoIds || []).entries()) {
        const record = await offline.db.get(offline.db.STORE_PHOTOS, pid)
        if (record?.blob) {
          // Оборачиваем в File: Blob из IndexedDB живёт в другом
          // realm, и FormData другого не принимает — тот же приём,
          // что в buildFormData приёмки.
          fd.append(
            `file_${i}`,
            new File([record.blob], `photo_${i}.jpg`, {
              type: record.type || 'image/jpeg',
            })
          )
          stored.push(pid)
        }
        // Снимок не найден (вытеснен квотой за 14 дней) — сообщение
        // всё равно уходит: текст ценнее, чем ничего.
      }

      const { data } = await api.post(
        `/chat/conversations/${p.conversation_id}/messages`,
        fd,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Idempotency-Key': task.idempotencyKey,
          },
        }
      )
      // И 201, и 200 (повтор после частичного успеха) — успех.

      await Promise.all(stored.map((pid) => photos.deletePhoto(pid)))
      onMessageSent?.(p.conversation_id, data?.data)
    },
  }
}

/**
 * Кэш чтения: обёртки платформенного модуля, чтобы экраны
 * не знали про IndexedDB напрямую.
 */
export const cacheConversation = (conversationId, items) =>
  messages.cacheMessages(conversationId, items)

export const cachedConversation = (conversationId) =>
  messages.getCachedMessages(conversationId)

/** Число неотправленных сообщений — для индикатора очереди. */
export async function pendingChatCount() {
  const tasks = await outbox.pending()
  return tasks.filter((t) => t.type === outbox.TYPE_SEND_MESSAGE).length
}
